/**
 * Role-based access control utilities for multi-tenant navigation
 */

export enum UserType {
  B2C = 'b2c',
  B2B2C_STUDENT = 'b2b2c_student',
  B2B2C_ADMIN = 'b2b2c_admin',
  B2B2C_MENTOR = 'b2b2c_mentor',
}

export enum UserRole {
  USER = 'user',
  ADMIN = 'admin',
  MODERATOR = 'moderator'
}

export interface User {
  id: number;
  email: string;
  full_name: string;
  role: UserRole;
  user_type: UserType;
  school_id?: number;
  is_active: boolean;
  subscription_status?: string;
  onboarding_completed: boolean;
}

export interface NavigationItem {
  name: string;
  title: string;
  icon: string;
  component: React.ComponentType<any>;
  accessRoles: UserType[];
  requiresSubscription?: boolean;
}

/**
 * Check if user can access a specific navigation item
 */
export const canAccessNavItem = (user: User, item: NavigationItem): boolean => {
  // Check user type access
  if (!item.accessRoles.includes(user.user_type)) {
    return false;
  }
  
  // Check subscription requirement
  if (item.requiresSubscription && user.subscription_status !== 'active') {
    return false;
  }
  
  return true;
};

/**
 * Check if user is a super admin (platform admin)
 */
export const isSuperAdmin = (user: User): boolean => {
  return user.role === UserRole.ADMIN && user.user_type === UserType.B2C;
};

/**
 * Check if user is a school admin
 */
export const isSchoolAdmin = (user: User): boolean => {
  // First check: if user is a superadmin, they are NOT a school admin
  if (isSuperAdmin(user)) {
    return false;
  }
  
  // School admins are either:
  // 1. B2B2C_ADMIN user type, OR
  // 2. Admin role with a school_id assigned
  return user.user_type === UserType.B2B2C_ADMIN || 
         (user.role === UserRole.ADMIN && user.school_id !== null);
};

/**
 * Check if user is a B2C customer
 */
export const isB2CUser = (user: User): boolean => {
  return user.user_type === UserType.B2C;
};

/**
 * Check if user is a school student
 */
export const isB2B2CStudent = (user: User): boolean => {
  return user.user_type === UserType.B2B2C_STUDENT;
};

/**
 * Check if user is a school mentor
 */
export const isB2B2CMentor = (user: User): boolean => {
  return user.user_type === UserType.B2B2C_MENTOR;
};

/**
 * Check if user is a school admin by user_type
 */
export const isB2B2CAdmin = (user: User): boolean => {
  return user.user_type === UserType.B2B2C_ADMIN;
};

/**
 * Check if user is a mentor or admin (can receive shared documents)
 */
export const isMentorOrAdmin = (user: User): boolean => {
  return isB2B2CMentor(user) || isSchoolAdmin(user) || isSuperAdmin(user);
};

/**
 * Check if user can share documents within their school
 */
export const canShareDocuments = (user: User): boolean => {
  return user.school_id != null && user.user_type !== UserType.B2C;
};

/**
 * Check if user can manage students
 */
export const canManageStudents = (user: User): boolean => {
  return isSuperAdmin(user) || isSchoolAdmin(user);
};

/**
 * Check if user can manage mentors (invite, view, remove)
 */
export const canManageMentors = (user: User): boolean => {
  return isSuperAdmin(user) || isSchoolAdmin(user);
};

/**
 * Check if user can upload RAG documents
 */
export const canUploadRagDocuments = (user: User): boolean => {
  return isSuperAdmin(user) || isSchoolAdmin(user);
};

/**
 * Check if user can access B2C RAG system
 */
export const canAccessB2CRag = (user: User): boolean => {
  return isB2CUser(user) || isSuperAdmin(user);
};

/**
 * Check if user can access school RAG system
 */
export const canAccessSchoolRag = (user: User): boolean => {
  return user.school_id !== null;
};

/**
 * Get user display name with role indicator
 */
export const getUserDisplayName = (user: User): string => {
  const roleSuffix = isSuperAdmin(user) ? ' (Platform Admin)' :
                    isSchoolAdmin(user) ? ' (School Admin)' :
                    isB2B2CMentor(user) ? ' (Mentor)' :
                    isB2B2CStudent(user) ? ' (Student)' : '';

  return `${user.full_name}${roleSuffix}`;
};

/**
 * Get appropriate dashboard title based on user type
 */
export const getDashboardTitle = (user: User): string => {
  if (isSuperAdmin(user)) {
    return 'Platform Dashboard';
  } else if (isSchoolAdmin(user)) {
    return 'School Admin Dashboard';
  } else if (isB2B2CStudent(user)) {
    return 'Student Dashboard';
  } else {
    return 'Dashboard';
  }
};

// ─── Capability helpers (encode docs/access-policy.md) ───

/**
 * Any B2B2C user whose billing is governed by their school.
 */
export const isSchoolManagedUser = (user: User): boolean =>
  isB2B2CAdmin(user) || isB2B2CStudent(user) || isB2B2CMentor(user);

/**
 * Only B2C (non-super-admin) users see the individual paywall.
 */
export const canAccessIndividualPaywall = (user: User): boolean =>
  isB2CUser(user) && !isSuperAdmin(user);

/**
 * School admins and super admins can manage school billing.
 */
export const canManageSchoolBilling = (user: User): boolean =>
  isSuperAdmin(user) || isSchoolAdmin(user);

/**
 * B2C, super admins and students require intake; school admins and mentors skip it.
 *
 * Super admins are intentionally included: "Redo onboarding" must send them
 * through the tour → intake funnel like a real user. Their billing is always
 * effectively active, so after intake they land straight in the app.
 */
export const requiresIntakeCheck = (user: User): boolean => {
  if (isSchoolAdmin(user) || isB2B2CMentor(user)) return false;
  return true; // B2C (incl. super admins) + students
};

/**
 * Mentors do NOT have their own ICF progress view.
 */
export const canViewOwnProgress = (user: User): boolean =>
  !isB2B2CMentor(user);

/**
 * Super admins, school admins, and mentors can view student progress.
 */
export const canViewStudentProgress = (user: User): boolean =>
  isSuperAdmin(user) || isSchoolAdmin(user) || isB2B2CMentor(user);

/**
 * Only super admins and school admins access the admin student list endpoint.
 */
export const canAccessAdminStudentList = (user: User): boolean =>
  isSuperAdmin(user) || isSchoolAdmin(user);

/**
 * Super admins, school admins, and mentors can upload materials.
 */
export const canUploadMaterials = (user: User): boolean =>
  isSuperAdmin(user) || isSchoolAdmin(user) || isB2B2CMentor(user);

/**
 * Only super admins access the platform admin screen.
 */
export const canAccessPlatformAdmin = (user: User): boolean =>
  isSuperAdmin(user);

/**
 * Only super admins see the Documents (RAG admin) tab.
 */
export const canAccessDocumentsTab = (user: User): boolean =>
  isSuperAdmin(user);

/**
 * Get appropriate chat title based on user context
 */
export const getChatTitle = (user: User): string => {
  // All users use the same brand name "Mentor Coaching AI"
  // The RAG context (school vs B2C) is handled in the backend
  return 'Mentor Coaching AI';
};
